import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MissionSubCard, MissionSubCardHorizontalDivider } from "./MissionLayoutComponents";
import { useAlert } from 'react-alert';
import { DateTime } from 'luxon';

import Button from "../../common_components/Button";
import Timeline from "./Timeline";
import ScheduleModal from "./ScheduleModal";
import CenteredLoadingIcon from "../../common_components/CenteredLoadingIcon";
import ConfirmationModal from "./ConfirmationModal";
import DotMenuIcon from "../../../../../../../assets/images/user_app/mission_page/ellipses_button.svg";


// API calls
import MissionEventAPI from "../../../api/api_calls/MissionEventAPI";
import ScheduledEventAPI from "../../../api/api_calls/ScheduledEventAPI";


function MissionScheduled({ mission, userMissionState, refreshMissionStatus }) {
    const navigate = useNavigate();
    const alert = useAlert();
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [availabilities, setAvailabilities] = useState([]);
    const [missionEvent, setMissionEvent] = useState(null);
    const [lookAheadDays, setLookAheadDays] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [timeZone, setTimeZone] = useState(null);
    const [scheduledEventsLoading, setScheduledEventsLoading] = useState(true);
    const [availabilitiesExist, setAvailabilitiesExist] = useState(false);

    // For current scheduled events
    const [scheduledEvent, setScheduledEvent] = useState(null);
    const [scheduledEventUrl, setScheduledEventUrl] = useState(null);
    const [availabilityLoading, setAvailabilityLoading] = useState(true);
    const [joinEnabled, setJoinEnabled] = useState(false);
    const [stillProcessing, setStillProcessing] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [submittingCancel, setSubmittingCancel] = useState(false);


    // ------------------------------------------------------------
    // -------------------- Use Effects ----------------------------
    // ------------------------------------------------------------

    // Fetch the availabilities for this mission. 
    useEffect(() => {
        async function fetchAvailabilities() {
            const response = await MissionEventAPI.fetch_availabilities(mission.id);

            if (response.success) {
                setAvailabilities(response.appointment_list);
                setAvailabilitiesExist(response.appointment_list.days.length > 0);
                setMissionEvent(response.mission_event);
                setLookAheadDays(response.look_ahead_days);
                setStartDate(response.appointment_list.start_date);
                setEndDate(response.appointment_list.end_date);
                setTimeZone(response.user_timezone);
            }
            setAvailabilityLoading(false);
        }

        async function fetchScheduledEvents() {
            const response = await ScheduledEventAPI.index(mission.id);

            if (response.success) {
                setScheduledEvent(response.upcoming_event);
                setScheduledEventUrl(response.upcoming_event_url);
                setJoinEnabled(isScheduledEventWithin10Min(response.upcoming_event.start_time, response.upcoming_event.end_time, response.timezone));
                setStillProcessing(response.still_processing);
            }
            setScheduledEventsLoading(false);
        }

        fetchAvailabilities();
        fetchScheduledEvents();
    }, []);

    function isScheduledEventWithin10Min(startTime, endTime, timeZone) {
        const now = DateTime.now().setZone(timeZone);
        const start = DateTime.fromISO(startTime, { zone: timeZone });
        const end = DateTime.fromISO(endTime, { zone: timeZone });

        return now >= start.minus({ minutes: 10 }) && now <= end;
    }


    // Check if the scheduled event is within 10 minutes of the start time.
    useEffect(() => {
        const interval = setInterval(() => {
            const isNowWithin10Min = isScheduledEventWithin10Min(scheduledEvent.start_time, scheduledEvent.end_time, timeZone);

            console.log("isNowWithin10Min", isNowWithin10Min);
            console.log("now", now);
            console.log("start", start);
            console.log("end", end);

            setJoinEnabled(isNowWithin10Min);
        }, 30000); // every 30 seconds

        return () => clearInterval(interval); // cleanup on unmount
    }, [scheduledEvent, timeZone]);


    // ------------------------------------------------------------
    // -------------------- Functions ----------------------------
    // ------------------------------------------------------------

    // Open the scheduled event URL.
    function openMeeting() {
        console.log("Opening meeting...");
        window.open(scheduledEventUrl, "_blank");
    }

    function rescheduleEvent() {
        if (availabilitiesExist) {
            setShowScheduleModal(true);
        } else {
            alert.error("There are no currently available time slots for this mission. Please cancel your session and try again later.")
        }
    }

    function cancelEvent() {
        const CancelAppointment = async () => {
            const response = await ScheduledEventAPI.cancel(mission.id, scheduledEvent.id, navigate);
            if (response.success) {
                alert.success("Appointment cancelled.")
                refreshMissionStatus();
            } else {
                alert.error("Error canceling scheduled event. Please try again or contact us.")
            }
            setSubmittingCancel(false);
        }
        if (!submittingCancel) {
            setSubmittingCancel(true);
            setShowConfirmationModal(false);
            CancelAppointment();
        }
    }

    // ------------------------------------------------------------
    // -------------------- Render ----------------------------
    // ------------------------------------------------------------

    return (
        <>
            <MissionSubCard>
                <Timeline mission={mission} userMissionState={userMissionState} />
                <MissionSubCardHorizontalDivider showMobile={true} />
                {/*  So there SHOULD be scheduled events in this state. */}
                {/* But we'll check anyway. If there are none, just show same stuff from the MissionSchedule component. */}
                {scheduledEventsLoading ? <CenteredLoadingIcon /> :
                    (scheduledEvent ?
                        (stillProcessing ?
                            <div className="mission-scheduled-events-container">
                                <ScheduledEventTiming scheduledEvent={scheduledEvent} timeZone={timeZone} />
                                <div className="mission-scheduled-events-actions-container">
                                    <div className="mission-schedule-processing-text">
                                        We're processing this session. Please check back in an hour.
                                    </div>
                                </div>
                            </div>
                            :
                            <div className="mission-scheduled-events-container">
                                <ScheduledEventTiming scheduledEvent={scheduledEvent} timeZone={timeZone} />
                                <div className="mission-scheduled-events-actions-container">
                                    <Button size="medium" color="primary" onClick={() => openMeeting()} disabled={!joinEnabled}>Join meeting</Button>
                                    {/* Placeholder for dot menu of actions. */}
                                    <div className="dropdown">
                                        <button className="btn btn-secondary mission-scheduled-events-actions-dropdown-button" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                            <img src={DotMenuIcon} alt="Dot menu" />
                                        </button>
                                        <ul className="dropdown-menu mission-scheduled-events-actions-dropdown-menu">
                                            <li className="dropdown-item mission-scheduled-events-actions-dropdown-item" onClick={() => rescheduleEvent()}>Reschedule</li>
                                            <li className="dropdown-item mission-scheduled-events-actions-dropdown-item" onClick={() => setShowConfirmationModal(true)}>Cancel</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )
                        :
                        <div className="mission-schedule-no-availabilities-text">We've encountered an error loading this event. Please check back later or contact us at info@despark.io</div>
                    )
                }
            </MissionSubCard>
            {!availabilityLoading && <ScheduleModal
                isOpen={showScheduleModal}
                missionId={mission.id}
                toggleModal={() => setShowScheduleModal(!showScheduleModal)}
                availabilities={availabilities}
                lookAheadDays={lookAheadDays}
                startDate={startDate}
                endDate={endDate}
                timeZone={timeZone}
                refreshMissionStatus={refreshMissionStatus}
            />}
            <ConfirmationModal
                isOpen={showConfirmationModal}
                toggleModal={() => setShowConfirmationModal(!showConfirmationModal)}
                confirmAction={cancelEvent}
            />
        </>
    )
}

function ScheduledEventTiming({ scheduledEvent, timeZone, formatType = "mission" }) {
    // Get date in format "Monday, Dec 12"
    const start = DateTime.fromISO(scheduledEvent.start_time, { zone: timeZone })
    const end = DateTime.fromISO(scheduledEvent.end_time, { zone: timeZone })

    const date = start.toFormat("cccc, LLL d");
    const startTime = start.toFormat("h:mma").toLowerCase();
    const endTime = end.toFormat("h:mma").toLowerCase();

    return (
        <div className={`mission-scheduled-events-timing-container ${formatType}`}>
            <h2>{date}</h2>
            <h2 className="mission-scheduled-events-timing-dot">•</h2>
            <h2 className="mission-scheduled-events-timing-time">{startTime} - {endTime}</h2>
        </div>
    )
}

export default MissionScheduled;
export { ScheduledEventTiming };